import {
  ActionButton,
  Badge,
  OfficeHeader,
  Paginator,
  RegularInsurerPicker,
  SearchSelectInput,
  Show,
  TableComponent,
  YearHeader,
} from "components";
import {
  Claim_status,
  Payment_status,
} from "_graphql/__generated__/globalTypes";
import { FC, useEffect, useMemo } from "react";
import { useAppNavigate, useAppSearch } from "_graphql/cache/auth";
import { useUrlState, withPermissions } from "utils";

import { Action } from "components/buttons/action-button";
import CreateContainer from "./create";
import FilterMenu from "./filter";
import { KEKOffers_rows } from "_graphql/queries/kek/__generated__/KEKOffers";
import NotifyContainer from "./notify";
import YearPicker from "layouts/calendar-wrapper/year-picker";
import moment from "moment";
import numeral from "numeral";
import { useFormik } from "formik";
import { useKEKOffers } from "hooks/data/use-offers";
import { usePagination } from "hooks";

type Props = {};

// const tabs = [{ name: "Unclaimable Offers ", href: "unclaimable" }];

const ClaimsPage: FC<Props> = () => {
  const [modal, setModal] = useUrlState("modal");
  const [orderStatus] = useUrlState("orderStatus");
  const [year] = useUrlState("year");
  const searchParams = useAppSearch();
  const navigate = useAppNavigate();
  const { offset, limit, setPage, search } = usePagination();

  const form = useFormik<any>({
    initialValues: {
      insurer: "",
      claim_status: "",
    },
    onSubmit: (values) => {},
  });

  const filter = useMemo(
    () => ({
      page: searchParams.page || 1,
      pageSize: searchParams.pageSize || 10,
      search: (search || "").toString(),
      args: {
        status: ["CLOSED"],
        insurer_id: searchParams.insurer ? searchParams.insurer : undefined,
        year: searchParams.year?.toString() || "",
        offer_from: searchParams.offer_from || "",
        class_of_business_id: searchParams.business
          ? searchParams.business
          : undefined,
        payment_status: searchParams.payment_status
          ? [searchParams.payment_status]
          : [],
        claim_status: searchParams.claim_status
          ? [searchParams.claim_status]
          : [],
        approval_status: searchParams.approval_status || undefined,
        expiry_status: searchParams.expiry_status || undefined,
        staff_id: searchParams.employee
          ? [Number(searchParams.employee)]
          : undefined,
      },
    }),
    [
      searchParams.insurer,
      searchParams.business,
      searchParams.payment_status,
      searchParams.approval_status,
      searchParams.claim_status,
      searchParams.page,
      searchParams.pageSize,
      searchParams.year,
      search,
    ]
  );

  const { offers, loading, refetch, count } = useKEKOffers(filter as any);

  const expandItem = (id: string) => () => {
    navigate({
      to: `./${id}/view`,
    });
  };

  const dispatchAction =
    (id: string, action: Exclude<Action, "expand" | "goto" | "clone">) =>
    () => {
      navigate({
        search: (old) => ({
          ...old,
          id,
          modal: action,
        }),
      });
    };

  useEffect(() => {
    if (form.values.insurer) {
      setPage(1);
    }
  }, [setPage, form.values.insurer]);

  return (
    <main className="flex-1 flex flex-col overflow-hidden bg-background-500">
      <YearHeader />
      {/* <Tabs
        tabs={tabs}
        orderStatus={orderStatus}
        defaultTab="Claimable Offers"
      /> */}
      <div className="flex flex-1 overflow-y-auto">
        <div className="flex-1 mx-auto min-w-0  py-4 sm:py-6 overflow-hidden overflow-y-auto light flex">
          <TableComponent
            hasSearch
            title={"offers"}
            loading={loading}
            columns={[
              {
                name: "Policy #",
                accessor: "offer_detail.policy_number",
                render: (value) => (
                  <span className="font-bold text-xs">
                    {value.offer_detail?.policy_number || "N/A"}
                  </span>
                ),
              },
              {
                name: "Insurance Company",
                accessor: "insurer.insurer_company_name",
                render: (value) => (
                  <div className="flex flex-col text-xs">
                    <span className="font-medium">
                      {value.insurer?.insurer_company_name || "N/A"}
                    </span>
                    <span className="font-light">
                      {value.insurer?.insurer_company_email || "N/A"}
                    </span>
                  </div>
                ),
              },
              {
                name: "Insured",
                accessor: "offer_detail.insured_by",
                render: (value) => {
                  const details = JSON.parse(
                    value?.offer_detail?.offer_details || "[]"
                  );
                  return (
                    <div className="flex flex-col text-xs ">
                      <span className="font-medium">
                        {value.offer_detail?.insured_by || "N/A"}
                      </span>
                      <span className="font-light">
                        {[
                          "Motor Comprehensive Fleet",
                          "Motor Comprehensive",
                          "Motor Comprehensive (Automobile Fac Facility)",
                        ].includes(value?.classofbusiness?.business_name || "")
                          ? details?.find(
                              (el: { [x: string]: string }) =>
                                el["keydetail"] === "Vehicle Reg No" ||
                                el["keydetail"] === "Vehicle Reg No."
                            )?.value
                          : ""}
                      </span>
                    </div>
                  );
                },
              },
              {
                name: "Class Of Business",
                accessor: "classofbusiness.business_name",
                render: (value) => (
                  <div className="flex flex-col text-xs">
                    <span className="font-medium">
                      {value.classofbusiness?.business_name || "N/A"}
                    </span>
                  </div>
                ),
              },
              {
                name: "Sum Insured",
                accessor: "sum_insured",
                render: (value) => (
                  <div className="flex flex-col text-xs">
                    <span className="font-medium">
                      {value?.offer_detail?.currency}{" "}
                      {numeral(value?.sum_insured).format("0,0 ") || "N/A"}
                    </span>
                  </div>
                ),
              },
              {
                name: "Premium",
                accessor: "premium",
                render: (value) => (
                  <div className="flex flex-col text-xs">
                    <span className="font-medium">
                      {value?.offer_detail?.currency}{" "}
                      {numeral(value?.premium).format("0,0 ") || "N/A"}
                    </span>
                  </div>
                ),
              },
              {
                name: "Total Securities",
                accessor: "participants",
                render: (value) => (
                  <div className="flex flex-col text-xs">
                    <span className="font-medium">
                      {value?.offer_participant?.length}
                    </span>
                  </div>
                ),
              },
              {
                name: "Claim Status",
                accessor: "claim_status",
                render: (value) => (
                  <div className="flex flex-col text-xs">
                    <Badge
                      status={
                        value?.claim_status === Claim_status.CLAIMED
                          ? "success"
                          : "warning"
                      }
                      label={value?.claim_status}
                    />
                  </div>
                ),
              },
              {
                name: "Payment Status",
                accessor: "payment_status",
                render: (value) => (
                  <div className="flex flex-col text-xs">
                    <Badge
                      status={
                        value?.payment_status === Payment_status.PAID
                          ? "success"
                          : value?.payment_status === Payment_status.UNPAID
                            ? "warning"
                            : "error"
                      }
                      label={value?.payment_status}
                    />
                  </div>
                ),
              },
              {
                name: "Period",
                accessor: "created_at",
                render: (value) =>
                  `${moment(
                    value?.offer_detail?.period_of_insurance_from
                  ).format("MM/DD/YYYY")} - ${moment(
                    value?.offer_detail?.period_of_insurance_to
                  ).format("MM/DD/YYYY")}`,
              },
              {
                name: "Offer Date",
                accessor: "created_at",
                render: (value) =>
                  moment(value?.created_at).format("MM/DD/YYYY"),
              },
              {
                name: "Created By",
                accessor: "employee.employee_first_name",
                render: (value) => (
                  <div className="flex flex-row leading-0">
                    <span>
                      {value?.employee?.employee_first_name}{" "}
                      {value?.employee?.employee_last_name}
                    </span>
                  </div>
                ),
              },
              {
                name: "Actions",
                accessor: "actions",
                render: (value) => (
                  <>
                    {orderStatus !== "unclaimable" &&
                      value?.claim_status === "CLAIMED" && (
                        <ActionButton
                          action="view"
                          tooltip="View Claims"
                          onClick={expandItem(value?.offer_id || "")}
                        />
                      )}
                    <ActionButton
                      action="alert"
                      tooltip="Claim Notification"
                      onClick={dispatchAction(value?.offer_id || "", "alert")}
                    />
                    {orderStatus !== "unclaimable" &&
                      withPermissions(["*:*", "offers:*", "offers:payment"])(
                        <ActionButton
                          action="calculate"
                          tooltip="Make Claim"
                          onClick={dispatchAction(
                            value?.offer_id || "",
                            "calculate"
                          )}
                        />
                      )}
                  </>
                ),
              },
            ]}
            data={offers}
            renderFilter={({ filterOpen, setFilterOpen }) => (
              <FilterMenu open={filterOpen} setOpen={setFilterOpen} />
            )}
            pagination={() => (
              <Paginator
                offset={offset}
                limit={limit}
                totalItems={count || 0}
                currentSize={offers.length}
                setOffset={setPage}
              />
            )}
            refetch={refetch}
          />
        </div>
      </div>

      {searchParams?.id && (
        <>
          <NotifyContainer
            open={modal === "alert"}
            setOpen={(val: boolean) => setModal(val ? "alert" : undefined)}
            refetch={refetch}
          />
          <CreateContainer
            open={modal === "calculate"}
            setOpen={(val: boolean) => setModal(val ? "calculate" : undefined)}
            refetch={refetch}
          />
        </>
      )}
    </main>
  );
};

export default ClaimsPage;
